import { Button } from "@/components/builder/button";
import { BuilderImageFit, BuilderImageFocalPoint, toCssImagePosition } from "@/components/builder/builder-image";
import { Typography } from "@/components/builder/typography";
import { LeftArrow } from "@/components/icons/left-arrow-icon";
import RightArrow from "@/components/icons/right-arrow-icon";
import getBuilderLocalizedValue from "@/i18n/get-builder-localized-value";
import { useLocale, useTranslations } from "@/libs/base/components/locale-provider";
import { cn } from "@/libs/base/utils/layout";
import { withChildren } from "@builder.io/react";
import { ReactNode } from "react";
import Carousel, { ButtonGroupProps, DotProps } from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

interface HeroCarouselProps {
  builderBlock: any;

  draggable: boolean;
  swipeable: boolean;
  pauseOnHover: boolean;
  autoPlay: boolean;
  autoPlaySpeed?: number;
  infinite: boolean;
  slidesToSlide: number;

  children: ReactNode;
}

const responsive = {
  all: {
    breakpoint: { max: 4000, min: 0 },
    items: 1,
  },
};

export const HeroCarousel = withChildren(function HeroCarousel(props: HeroCarouselProps) {
  const defaultAutoPlaySpeed = 3000;

  return (
    <div className="relative w-full">
      <Carousel
        responsive={responsive}
        partialVisible
        ssr
        arrows={false}
        swipeable={props.swipeable}
        draggable={props.draggable}
        keyBoardControl
        showDots
        renderDotsOutside
        customDot={<HeroCarouselDot />}
        dotListClass="space-x-2.5 py-8.5 px-4.5 box-content absolute bottom-0 sm:right-0 sm:left-auto left-0 flex w-36 items-center justify-between sm:px-8"
        renderButtonGroupOutside
        customButtonGroup={<HeroCarouselButtons />}
        additionalTransfrom={0}
        slidesToSlide={props.slidesToSlide}
        autoPlay={props.autoPlay}
        autoPlaySpeed={props.autoPlaySpeed ?? defaultAutoPlaySpeed}
        infinite={props.infinite}
        rewind={false}
        rewindWithAnimation={false}
        pauseOnHover={props.pauseOnHover}
      >
        {props.children ? props.children : <></>}
      </Carousel>
    </div>
  );
});

function HeroCarouselDot({ active, index, onClick }: DotProps) {
  const {
    builder: {
      hero_carousel: { show_item },
    },
  } = useTranslations();

  return (
    <button
      type="button"
      className={cn("h-px.5 w-full cursor-pointer rounded-full transition", active ? "bg-white" : "bg-white/50")}
      onClick={() => onClick?.()}
      aria-label={index === undefined ? undefined : show_item.replace("$1", (index + 1).toString())}
      aria-current={active ? "true" : undefined}
    />
  );
}

function HeroCarouselButtons({ next, previous }: ButtonGroupProps) {
  const {
    builder: { hero_carousel: translations },
  } = useTranslations();

  return (
    <div className="absolute bottom-0 right-0 flex h-fit gap-7.5 p-4 sm:top-0 sm:p-9">
      <button
        type="button"
        className="text-white transition disabled:opacity-0"
        onClick={() => previous?.()}
        aria-label={translations.previous_item}
      >
        <LeftArrow className="h-9 w-9" strokeWidth={1} />
      </button>
      <button
        type="button"
        className="text-white transition disabled:opacity-0"
        onClick={() => next?.()}
        aria-label={translations.next_item}
      >
        <RightArrow className="h-9 w-9" strokeWidth={1} />
      </button>
    </div>
  );
}

export function HeroCarouselItem({
  image,
  title,
  description: rawDescription,
  link,
  linkText,
  textPosition,
  imagePosition,
  imageFit,
}: {
  image: string;
  title?: string | BuilderLocalizedValue;
  description?: string | BuilderLocalizedValue;
  link?: string;
  linkText?: string | BuilderLocalizedValue;
  textPosition?: "Top left" | "Bottom left" | "Centered";
  imagePosition?: BuilderImageFocalPoint;
  imageFit?: BuilderImageFit;
}) {
  const locale = useLocale();

  const description = rawDescription === undefined ? null : getBuilderLocalizedValue(rawDescription, locale);
  const titleText = title === undefined ? undefined : getBuilderLocalizedValue(title, locale);
  const contain = imageFit === "Contain";

  return (
    // TODO: Reevaluate if we should hardcode these in pixels
    <div
      className={cn("relative flex min-h-[450px] bg-no-repeat p-4 sm:min-h-[768px] sm:p-9", {
        "items-center justify-center sm:px-64": textPosition === "Centered",
        "items-start justify-start": textPosition === "Top left",
        "items-end justify-start": textPosition === "Bottom left",
        "pb-24": textPosition !== "Centered",
        "bg-black": contain,
      })}
      style={{
        backgroundImage: `url(${image})`,
        backgroundPosition: toCssImagePosition(imagePosition),
        backgroundSize: contain ? "contain" : "cover",
      }}
      role="img"
      aria-label={titleText ?? undefined}
    >
      <div
        className={cn(
          "flex flex-col gap-4 text-white sm:gap-6",
          textPosition === "Centered" ? "items-center" : "max-w-80",
        )}
      >
        {titleText && (
          <Typography
            text={titleText}
            size="Header"
            variant="Normal"
            className={cn(textPosition === "Centered" && "text-center")}
          />
        )}

        {description && (
          <div
            className={cn("text-headline sm:text-subtitle", textPosition === "Centered" && "text-center")}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        {link && linkText && (
          <Button
            variant="Filled"
            background="Grey"
            shape="Rectangle"
            text={getBuilderLocalizedValue(linkText, locale)}
            href={link}
            hasArrow
            className="w-fit"
          />
        )}
      </div>
    </div>
  );
}
